import { Deal, PageLayout, Callout } from '../types/deal';

export type PromoPage = 'welcome' | 'login' | 'signup' | 'verify' | 'redeem' | 'success';

// Which image group each page belongs to.
const WELCOME_GROUP: PromoPage[] = ['welcome', 'success'];

const isWelcomeGroup = (page: PromoPage): boolean => WELCOME_GROUP.includes(page);

/**
 * Resolve the image reference for a given promo page, applying the fallback
 * cascade:
 *   - welcome / success: welcomeImage → authImage → images[0]
 *   - login / signup / verify / redeem: authImage → images[0]
 */
export const getPageImage = (deal: Deal | null | undefined, page: PromoPage): string | undefined => {
  if (!deal) return undefined;
  const defaultImage = deal.images?.[0];
  if (isWelcomeGroup(page)) {
    return deal.welcomeImage || deal.authImage || defaultImage;
  }
  return deal.authImage || defaultImage;
};

/**
 * Layout config for the page's image group (or undefined for default cover/center).
 */
export const getPageLayout = (deal: Deal | null | undefined, page: PromoPage): PageLayout | undefined => {
  if (!deal?.imageLayout) return undefined;
  return isWelcomeGroup(page) ? deal.imageLayout.welcome : deal.imageLayout.auth;
};

/**
 * Convert a layout config into inline styles for the hero <img>. Returns an
 * empty object when there's no layout, preserving the default cover/center.
 */
/**
 * Returns the deal's callout if it's enabled for the given page (page is listed
 * in callout.pages and there's a title or body to show), else null.
 */
export const getCallout = (deal: Deal | null | undefined, page: PromoPage): Callout | null => {
  const c = deal?.callout;
  if (!c) return null;
  if (!c.pages?.includes(page)) return null;
  if (!c.title && !c.body) return null;
  return c;
};

export const layoutToStyle = (layout: PageLayout | undefined): React.CSSProperties => {
  if (!layout) return {};
  return {
    objectFit: layout.fit,
    objectPosition: `${layout.posX}% ${layout.posY}%`,
    transform: layout.zoom && layout.zoom !== 100 ? `scale(${layout.zoom / 100})` : undefined,
  };
};
